/**
 * MobileStores — Stores · Restaurants · Farms · Charities directory.
 *
 * Sub-tabs let users pivot between store types (each opens a filtered
 * view — Stores default). Search + Category + Country/State/City +
 * Pincode filters mirror the mobile mockup. Card layout: logo/photo
 * on the left, name/address/tag on the right, distance top-right.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { MapPin, Search } from "lucide-react";
import MobileLocationBar from "./MobileLocationBar";
import { CATEGORY_TILES, CategoryTile } from "./MobileHome";
import { categoryIllustration } from "@/config/categoryIllustrations";

import BottomSheetPicker from "./BottomSheetPicker";
import { fetchCountries, fetchStates, fetchCities } from "./geoPickers";

const API = process.env.REACT_APP_BACKEND_URL;
const FP_PURPLE = "#653091";
const FP_GREEN = "#9CCA45";

const SUB_TABS = [
  { key: "stores",       label: "Stores",       types: "supermarket,grocery,convenience_store,health_food,deli,butcher,bakery,seafood_shop,cheese_shop,chocolate_shop,confectionery,spice_shop,tea_shop,beverages_shop,wine_shop,alcohol_shop,brewery,winery,distillery,ice_cream" },
  { key: "restaurants",  label: "Restaurants",  types: "restaurant,fast_food,food_court,cafe,coffee_shop,bar,pub,food_truck" },
  { key: "farms",        label: "Farms",        types: "farm,farmers_market,csa" },
  { key: "charities",    label: "Charities",    types: "food_bank,food_pantry,food_charity,soup_kitchen" },
];

function Opener({ value, placeholder, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="w-full flex items-center justify-between border border-gray-100 rounded-2xl bg-white shadow-sm px-4 py-3.5 text-left"
    >
      <span className={`text-base font-medium ${value ? "text-gray-900" : "text-gray-500"}`}>
        {value || placeholder}
      </span>
      <span className="opacity-40">▾</span>
    </button>
  );
}

export default function MobileStores() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("t") || "stores";
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [zip, setZip] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sheet, setSheet] = useState(null);

  const activeTab = SUB_TABS.find((t) => t.key === tab) || SUB_TABS[0];

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/food-businesses`, {
      params: {
        q: q || undefined,
        business_type: activeTab.types,
        state: state || undefined,
        city: city || undefined,
        zip: zip || undefined,
        limit: 30,
      },
    })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [tab, q, state, city, zip]); // eslint-disable-line

  const setTab = (k) => {
    const p = new URLSearchParams(params); p.set("t", k); setParams(p, { replace: true });
  };

  return (
    <div data-testid="mobile-stores">
      {/* Category tiles with Stores active */}
      <div
        className="grid grid-cols-4 gap-2 px-3 pt-3 pb-4"
        style={{ background: "linear-gradient(180deg,#B5DA5E 0%,#CBEB90 100%)" }}
      >
        {CATEGORY_TILES.map((t) => (
          <CategoryTile key={t.key} tile={t} active={t.key === "stores"} />
        ))}
      </div>

      {/* Sub-tabs */}
      <div className="mt-3 px-4 flex gap-2 overflow-x-auto" data-testid="stores-subtabs">
        {SUB_TABS.map((t) => {
          const active = t.key === tab;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`stores-tab-${t.key}`}
              className="px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap"
              style={{
                background: active ? FP_PURPLE : "#fff",
                color: active ? "#fff" : "#1c1c1c",
                boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Location */}
      <div className="flex items-center justify-center mt-3 mb-3">
        <MobileLocationBar testid="stores-location" />
      </div>

      {/* Filters */}
      <div className="px-4 space-y-3">
        <div className="flex items-center gap-2 bg-white rounded-xl shadow-sm border border-gray-200 px-3 py-3">
          <input
            data-testid="stores-search-input"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search Stores/Farms/Vendors"
            className="flex-1 outline-none bg-transparent text-base"
          />
          <Search className="w-4 h-4 opacity-60" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Opener value="United States" placeholder="Select Country" onClick={() => setSheet("country")} testId="stores-filter-country" />
          <Opener value={state} placeholder="Select State" onClick={() => setSheet("state")} testId="stores-filter-state" />
          <Opener value={city} placeholder="Select City" onClick={() => setSheet("city")} testId="stores-filter-city" />
          <label className="relative flex items-center rounded-2xl bg-white shadow-sm border border-gray-100">
            <input
              data-testid="stores-filter-zip"
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              placeholder="Enter Pincode"
              className="w-full bg-transparent px-3 py-3 text-base outline-none"
            />
          </label>
        </div>
      </div>

      {/* Results */}
      <div className="mt-4 px-4 pb-16 space-y-3" data-testid="stores-list">
        {loading ? (
          <div className="text-sm opacity-60">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 opacity-60 text-sm">No {activeTab.label.toLowerCase()} match your filters.</div>
        ) : (
          items.map((b) => {
            const cover = (b.images || [])[0] || categoryIllustration(b.business_type, b.slug, {
              subType: b.sub_type, cuisines: b.cuisines, name: b.name,
            });
            const miles = b.distance_miles ? `${b.distance_miles.toFixed(2)} miles` : null;
            const typeLabel = b.business_type ? b.business_type.replace(/_/g, " ") : "Food · Dining";
            return (
              <Link
                to={`/food-pulse/business/${b.slug}`}
                key={b.slug}
                data-testid={`stores-item-${b.slug}`}
                className="flex items-stretch gap-3 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
              >
                {cover && <img src={cover} alt={b.name} loading="lazy" className="w-20 h-20 object-cover flex-shrink-0 self-center ml-2 rounded-lg" />}
                <div className="flex-1 min-w-0 py-3 pr-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-base font-bold truncate">{b.name}</div>
                    {miles && <div className="text-xs font-semibold whitespace-nowrap" style={{ color: FP_PURPLE }}>{miles}</div>}
                  </div>
                  {b.address && (
                    <div className="flex items-center gap-1 text-xs text-gray-600 mt-0.5">
                      <MapPin className="w-3 h-3" /> <span className="truncate">{b.address}</span>
                    </div>
                  )}
                  <div className="text-xs mt-1 opacity-80">Food · Dining / {typeLabel}</div>
                </div>
              </Link>
            );
          })
        )}
      </div>

      {/* Bottom sheets */}
      <BottomSheetPicker
        open={sheet === "country"}
        title="Select Country"
        fetchOptions={fetchCountries}
        onSelect={() => {}}
        onClose={() => setSheet(null)}
        testIdPrefix="stores-country"
      />
      <BottomSheetPicker
        open={sheet === "state"}
        title="Select State"
        fetchOptions={(query) => fetchStates("United States", query)}
        onSelect={(v) => { setState(v); setCity(""); }}
        onClose={() => setSheet(null)}
        testIdPrefix="stores-state"
      />
      <BottomSheetPicker
        open={sheet === "city"}
        title="Select City"
        fetchOptions={(query) => fetchCities("United States", state, query)}
        onSelect={(v) => setCity(v)}
        onClose={() => setSheet(null)}
        testIdPrefix="stores-city"
      />
    </div>
  );
}
